// CDL Skills Mastery Lab - Complete Content Database
// Massachusetts Bay Transportation Authority
// Version 1.0, 4/10/2026
// Pages 1-10 in exact order provided

export const CDL_MASTERY_LAB_CHAPTERS = [
  {
    id: 0,
    title: "Vehicle Inspection & Glossary",
    pages: [
      {
        pageNum: 1,
        title: "CDL Skills Mastery Lab - Cover",
        section: "Cover Page",
        content: `CDL Skills Mastery Lab

STUDENT GUIDE

Massachusetts Bay Transportation Authority

Version 1.0
Course Code:

This workbook contains comprehensive training for commercial driver licensing with real bus photography and operational guidelines.`,
        hasVisuals: true,
        isCover: true,
        images: [
          {
            type: "cover",
            alt: "CDL Skills Mastery Lab Cover Page",
            description: "Professional cover page featuring bus tail lights, exterior side view, and interior dashboard imagery",
            hasActualImage: true
          }
        ],
        coverElements: {
          title: "CDL Skills Mastery Lab",
          subtitle: "STUDENT GUIDE",
          organization: "Massachusetts Bay Transportation Authority",
          version: "Version 1.0",
          images: [
            { position: "top", description: "Red bus tail lights and rear section" },
            { position: "middle", description: "Bus exterior side profile view" },
            { position: "bottom", description: "Bus interior dashboard and controls" }
          ]
        }
      },
      {
        pageNum: 2,
        title: "Table of Contents",
        section: "Table of Contents",
        content: `Table of Contents`,
        hasVisuals: false,
        isTOC: true,
        tocSections: [
          {
            label: "Course Overview",
            color: "#3498db",
            entries: [
              { title: "Course CDL Skill Mastery Lab", page: 6 },
              { title: "Introduction", page: 6 },
              { title: "Introduction", page: 8 }
            ]
          },
          {
            label: "Vehicle Inspection & Procedures",
            color: "#2980b9",
            entries: [
              { title: "Vehicle Inspection", page: 8 },
              { title: "Air Brake Inspection", page: 13 },
              { title: "Pre-trip Inspection", page: 27 }
            ]
          },
          {
            label: "Skills Testing",
            color: "#27ae60",
            entries: [
              { title: "Basic Control Skill Test", page: 28 },
              { title: "Road Test Courses", page: 36 },
              { title: "Special Procedures", page: 39 }
            ]
          },
          {
            label: "Advanced Maneuvers",
            color: "#e67e22",
            entries: [
              { title: "Forward Steer Tracking", page: 42 },
              { title: "Forward Steer", page: 45 },
              { title: "Go-Ons", page: 46 },
              { title: "Identify your Pivot Points", page: 48 }
            ]
          },
          {
            label: "Practice & Resources",
            color: "#8e44ad",
            entries: [
              { title: "Behind-the-Wheel Test Days", page: 50 },
              { title: "Simulator Days", page: 51 },
              { title: "Activity Status", page: 56 },
              { title: "CDL Video Library", page: 57 }
            ]
          },
          {
            label: "Assessments & References",
            color: "#c0392b",
            entries: [
              { title: "Completion Labeling", page: 63 },
              { title: "Pre-Trip Inspection Fill-the-Blank Practice", page: 64 },
              { title: "Air Brake Inspection", page: 70 },
              { title: "Pre-Trip Drills", page: 75 },
              { title: "Pre-Trip", page: 78 }
            ]
          }
        ]
      },
      {
        pageNum: 3,
        title: "Training Guide Icons",
        section: "Training Guide",
        content: `Training Guide Icons

These icons are cues for information types and activities in this document`,
        hasVisuals: true,
        isIconGuide: true,
        icons: [
          {
            icon: "warning",
            label: "Warning",
            description: "Information related to critical tasks and safety measures."
          },
          {
            icon: "safety",
            label: "Safety",
            description: "Important safety note."
          },
          {
            icon: "activity",
            label: "Activity",
            description: "Activities to practice what was learned."
          },
          {
            icon: "discussion",
            label: "Discussion",
            description: "Classroom or group discussion about what was learned."
          },
          {
            icon: "video",
            label: "Video",
            description: "Accompanying video resource."
          },
          {
            icon: "resources",
            label: "Resources",
            description: "Information related to critical tasks and safety measures."
          }
        ]
      }
    ]
  },
  {
    id: 1,
    title: "CDL Skills Test Overview",
    pages: [
      {
        pageNum: 4,
        title: "Introduction",
        section: "Introduction",
        content: `Introduction

Welcome to MBTA CDL Skills Mastery Lab

Welcome to MBTA's CDL Skills Mastery Lab. The CDL Skills Mastery Lab is designed to bridge the gap between CDL theory and hands-on performance. In this course, you will practice and master the critical skills required for the CDL Permit exam, including:

• Vehicle Inspection - Pre-trip, in-cab, external, engine compartment, and the Massachusetts required air brake check
• Basic Controls Skills - Forward line backing, forward offset tracking, and reverse check backing
• Road Test Skills - Forward line, Straight line braking, Forward offset tracking, and reverse offset backing
• Simulator Training - Corrective braking - Corridors scenario-based practice to sharpen decision-making

Throughout this program you'll rotate through classroom instruction, simulator time, guided demonstrations, peer skills, and hands-on practice. You need to practice you need to do, and how you need to perform under exam conditions

Safety Briefing

Safety is a key component of the learning experience. At the start of each class day the instructor will conduct a Safety Briefing, assigning materials to the class as a safety topic and identifying safety measures of the training center. The topics that must be covered are:

• CPR/First Aid - Certifie and qualifying certificate
• Emergency point of contact 911 OCC and ambularce
• AED - Obtain and administer AED
• Headcount - Single point of contact to conduct a headcount at the designated meeting location and report on anyone missing
• Fire Extinguisher - Obtain the fire extinguisher and put out fires`,
        hasVisuals: true,
        visualElements: [
          {
            type: "box",
            label: "Vehicle Inspection",
            icon: "🔍"
          },
          {
            type: "box",
            label: "Basic Control Skills",
            icon: "🎯"
          },
          {
            type: "box",
            label: "Road Test Skills",
            icon: "🛣️"
          },
          {
            type: "box",
            label: "Simulator Training",
            icon: "💻"
          }
        ],
        visualDescription: "Introduction with four key training components"
      }
    ]
  },
  {
    id: 2,
    title: "Overview & Introduction",
    pages: [
      {
        pageNum: 5,
        title: "Agenda",
        section: "Agenda",
        content: `How to Use These Sections

Sections 1-4 will be completed by all students and will take place at the start of the CDL exam sequence. These sections cover the essential components of the CDL Skills Exam test and must be completed before prerequisites can be completed and support and supervision.

Sections 5-6 may be rescheduled freely depending on classroom space, similar to any group tests. Many of the activities in this class are independently completed and support and supervision.

These sections work together to prepare you with the knowledge hands-on practice and confidence needed to successfully complete the CDL Skills Exam

Documents

Commercial Driver License Manual
CDL Theory/ELDT Student Guide`,
        hasVisuals: false,
        hasTable: true,
        pageHeader: "Agenda          This course contains the following sessions",
        tableData: {
          title: "Sections",
          headers: [""],
          rows: [
            ["1. Vehicle Inspection"],
            ["2. Brake Control Skills"],
            ["3. Road Test"],
            ["4. CDL Test Day"],
            ["5. Simulator Day"],
            ["6. Activities"]
          ]
        }
      },
      {
        pageNum: 7,
        title: "CDL Skills Test Overview",
        section: "CDL Skills Test Overview",
        content: `CDL Skills Test Overview

The CDL Skills Test is here part. A HM: CDL Manual

Vehicle Inspection Section
Purpose: Show knowledge of how to determine the vehicle is safe to drive
Requirements
- Name point both checked all CDL checklist items
- Use of CDL checklist as completed
- Noise Violations
- Use of the CDL checklist to contribute to the Basic Control Skills Test
Special Notes
- testing in a vehicle with air brakes (you must also prefer)
- described in the CDL Manual Preface)

Basic Control Skills Test (Section 12M - CDL Manual)
Purpose: Demonstrate control of the vehicle and ability to judge position relative to objects.
- Forward Steer Backing
- Straight Line Backing
- Forward Offset Backing
- Reverse Offset Tracking
- Must pass Vehicle Inspection and Basic Control Skills before proceeding to the Road Test

Road Test (Section 13 - CDL Manual)
Purpose: Evaluate the safe operation of a commercial vehicle
Key Errors
- Shift and turn completely
- Safely through intersections
- Show control through intersection curves and turns
- Over all rules of the road and show sound judgment and control

Train Timing
- Vehicle Inspection: 30 minutes
- Road Test: 90 minutes

Overall Duration: 90 minutes`
      },
    ]
  },
  {
    id: 3,
    title: "Course Content",
    pages: [
    ]
  }
];

export const getCDLChapters = () => CDL_MASTERY_LAB_CHAPTERS;

export const getAllCDLPages = () => {
  return CDL_MASTERY_LAB_CHAPTERS.flatMap(chapter => chapter.pages);
};

export const searchCDLContent = (query) => {
  const allPages = getAllCDLPages();
  const searchTerm = query.toLowerCase();
  return allPages.filter(page =>
    page.title.toLowerCase().includes(searchTerm) ||
    page.section.toLowerCase().includes(searchTerm) ||
    page.content.toLowerCase().includes(searchTerm)
  );
};
